/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IAddonTestPricing, TestAddonType } from "@/types/subscription.types";
import {
  Activity,
  Check,
  DollarSign,
  Inbox,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { CounterInput } from "@/components/counter-input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useBuyAddonTests,
  useGetAddonTestPricing,
} from "@/hooks/use-subscription";
import { extractErrorMessage } from "@/lib/utils";
import { toast } from "sonner";

const BasePrices = {
  [TestAddonType.EMAIL_HEALTH_TEST]: 0.10, // Starter tier price per credit
  [TestAddonType.INBOX_PLACEMENT_TEST]: 0.15, // Starter tier price per credit
};

type IAddonTestPricingWithDiscount = IAddonTestPricing & { 
  discount: number;
  pricePerTest: number;
};

function calculateDiscount(basePrice: number, discountedPrice: number) {
  if (basePrice <= 0 || discountedPrice < 0) {
    throw new Error("Invalid prices provided.");
  }

  // If discounted price is higher than or equal to base, there's no discount (0%)
  if (discountedPrice >= basePrice) {
    return 0;
  }

  const discount = ((basePrice - discountedPrice) / basePrice) * 100;
  return Math.round(discount * 100) / 100;
}

// Format price to show 2-4 decimal places, removing trailing zeros
function formatPricePerCredit(price: number): string {
  // Format to 4 decimal places to capture precision
  const formatted = price.toFixed(4);
  // Remove trailing zeros, but keep at least 2 decimal places for currency
  const withoutTrailingZeros = formatted.replace(/0+$/, '');
  const parts = withoutTrailingZeros.split('.');
  // If we have less than 2 decimal places, use 2 decimal places
  if (parts[1] && parts[1].length < 2) {
    return price.toFixed(2);
  }
  // Otherwise return without trailing zeros
  return withoutTrailingZeros.replace(/\.$/, '') || price.toFixed(2);
}

type steps = "addon-selection" | "quantity-selection" | "purchase-confirmation";

const BuyMoreTestsDialog = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<steps>("addon-selection");
  const [testType, setTestType] = useState<TestAddonType>(
    TestAddonType.EMAIL_HEALTH_TEST
  );

  const [selectedTier, setSelectedTier] =
    useState<IAddonTestPricingWithDiscount | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const { data: pricing, isLoading: isLoadingPricing } =
    useGetAddonTestPricing(testType);

  const {
    mutateAsync: createAddonTestSession,
    isPending: isLoadingCreateAddonTestSession,
  } = useBuyAddonTests();

  const transformedPricing: IAddonTestPricingWithDiscount[] | undefined =
    useMemo(() => {
      return pricing?.map((tier) => {
        // Calculate price per test: unitAmountDollars is total price for the tier
        // Since minQuantity === maxQuantity, use minQuantity for calculation
        const tierQuantity = tier.minQuantity ?? 1;
        const pricePerTest = tier.unitAmountDollars / tierQuantity;
        
        // Calculate discount based on price per test
        const discount = calculateDiscount(
          BasePrices[testType],
          pricePerTest
        );
        
        return {
          ...tier,
          discount,
          pricePerTest,
        };
      });
    }, [pricing, testType]);

  // Calculate pricing details based on current selection
  const calculateOrderSummary = () => {
    if (!selectedTier) return null;

    // Since tiers have fixed quantities, use the tier's quantity
    // unitAmountDollars is the total price for the tier
    const tierQuantity = selectedTier.minQuantity ?? quantity;
    const pricePerTest = selectedTier.pricePerTest;
    const totalPrice = selectedTier.unitAmountDollars; // Total price for the tier
    
    // Calculate what the original price would be at base price
    const originalTotalPrice = tierQuantity * BasePrices[testType];
    const discountAmount = originalTotalPrice - totalPrice;
    const discountPercentage = selectedTier.discount;

    return {
      quantity: tierQuantity,
      discount: discountPercentage,
      pricePerTest,
      totalPrice,
      discountAmount,
      originalTotalPrice,
    };
  };

  // Handlers for navigation
  const handleSelectTestType = (type: TestAddonType) => {
    setTestType(type);
    setStep("quantity-selection");
  };

  const handleSelectRange = (tier: IAddonTestPricingWithDiscount) => {
    setSelectedTier(tier);
    // Since tiers have fixed quantities, set quantity to the tier's fixed quantity
    setQuantity(tier.minQuantity ?? 1);
  };

  const handleBack = () => {
    if (step === "purchase-confirmation") {
      setStep("quantity-selection");
    } else if (step === "quantity-selection") {
      setStep("addon-selection");
      setTestType(TestAddonType.EMAIL_HEALTH_TEST);
    } else {
      setOpen(false);
    }
  };

  const resetDialog = () => {
    setStep("addon-selection");
    setTestType(TestAddonType.EMAIL_HEALTH_TEST);
    setSelectedTier(null);
    setQuantity(1);
  };

  const handlePurchase = async () => {
    try {
      if (!selectedTier) {
        toast.error("Please select a test tier");
        return;
      }

      // Use the tier's fixed quantity since tiers have fixed quantities
      const purchaseQuantity = selectedTier.minQuantity ?? quantity;

      window.cioanalytics?.track("buy_more_tests_checkout", {
        type: testType,
        quantity: purchaseQuantity,
      });

      await createAddonTestSession({
        addonId: selectedTier?._id,
        quantity: purchaseQuantity,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        extractErrorMessage(error, "Failed to create addon test session")
      );
    }
  };

  const orderSummary = calculateOrderSummary();

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          resetDialog();
          setOpen(true);
        }}
      >
        <ShoppingCart className="h-4 w-4" />
        Buy Add-Ons
      </Button>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetDialog();
        }}
      >
        <DialogContent className="px-0">
          <DialogHeader className="px-6">
            <DialogTitle>
              {step === "addon-selection" && "Select Test Type"}
              {step === "quantity-selection" &&
                `Select ${
                  testType === TestAddonType.EMAIL_HEALTH_TEST
                    ? "Email Health"
                    : "Inbox Placement"
                } Test Range`}
              {step === "purchase-confirmation" &&
                `Buy ${
                  testType === TestAddonType.EMAIL_HEALTH_TEST
                    ? "Email Health"
                    : "Inbox Placement"
                } Tests`}
            </DialogTitle>
            <DialogDescription>
              {step === "addon-selection" &&
                "Choose which type of test you want to purchase"}
              {step === "quantity-selection" &&
                "Select the quantity range for your tests"}
              {step === "purchase-confirmation" &&
                `Configure your tests purchase`}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[min(700px,60svh)] px-6">
            {/* Step 1: Select Test Type */}
            {step === "addon-selection" && (
              <div className="grid grid-cols-1 gap-4">
                <Card
                  className="flex flex-col cursor-pointer hover:border-primary transition-all"
                  onClick={() =>
                    handleSelectTestType(TestAddonType.EMAIL_HEALTH_TEST)
                  }
                >
                  <CardHeader className="p-4 pb-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="size-8 rounded-lg border  flex items-center justify-center">
                        <Activity className="size-4" />
                      </div>
                      Email Health Test
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* <div className="text-sm font-medium text-blue-600">
                        Improve deliverability instantly
                      </div> */}
                      <p className="text-sm text-muted-foreground">
                        Evaluate critical domain health factors like
                        authentication, security, and sender reputation.
                      </p>
                      <ul className="text-xs space-y-1 text-muted-foreground">
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Detect authentication problems
                        </li>
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Protects Against Deliverability Issues
                        </li>
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Provides Actionable Insights
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className="flex flex-col cursor-pointer hover:border-primary transition-all"
                  onClick={() =>
                    handleSelectTestType(TestAddonType.INBOX_PLACEMENT_TEST)
                  }
                >
                  <CardHeader className="p-4 pb-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <div className="size-8 rounded-lg border  flex items-center justify-center">
                        <Inbox className="size-4" />
                      </div>
                      Inbox Placement Test
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* <div className="text-sm font-medium text-green-600">
                        Know exactly where emails land
                      </div> */}
                      <p className="text-sm text-muted-foreground">
                        See if your emails are reaching inbox, spam folder, or
                        not arriving at all
                      </p>
                      <ul className="text-xs space-y-1 text-muted-foreground">
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Test across major email providers
                        </li>
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Get actionable placement insights
                        </li>
                        <li className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-500" />
                          Measure improvement over time
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 2: Select Range */}
            {step === "quantity-selection" && (
              <div className="space-y-4">
                {isLoadingPricing ? (
                  <div className="flex items-center justify-center h-full py-10 gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    <p className="text-sm text-muted-foreground">
                      Loading pricing...
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {transformedPricing?.map((tier) => (
                      <Collapsible
                        key={tier._id}
                        open={selectedTier?._id === tier._id}
                      >
                        <Card className=" hover:border-primary transition-colors">
                          <CollapsibleTrigger className="w-full">
                            <CardHeader
                              className="p-4 sm:space-x-0 flex-row justify-between items-center w-full"
                              onClick={() => handleSelectRange(tier)}
                            >
                              <div>
                                {tier.discount === 0 ? (
                                  <>
                                    <div className="font-medium">
                                      {tier.minQuantity === tier.maxQuantity
                                        ? `${tier.minQuantity} tests`
                                        : tier.maxQuantity !== null
                                        ? `${tier.minQuantity} - ${tier.maxQuantity} tests`
                                        : `${tier.minQuantity}+ tests`}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      No discount
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex flex-col items-start">
                                    <div className="font-medium">
                                      {tier.minQuantity === tier.maxQuantity
                                        ? `${tier.minQuantity} tests`
                                        : tier.maxQuantity !== null
                                        ? `${tier.minQuantity} - ${tier.maxQuantity} tests`
                                        : `${tier.minQuantity}+ tests`}
                                    </div>
                                    <div className="text-sm text-green-500">
                                      {tier.discount}% discount
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="text-right font-mono flex items-center">
                                <DollarSign className="size-4" />
                                {tier.pricePerTest.toFixed(2)}/test
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <CardContent className="p-4 flex flex-col gap-3 items-end justify-between border-t">
                              <div className="w-full space-y-1">
                                <Label>Quantity : {tier.minQuantity} tests</Label>
                               
                                {tier.minQuantity !== tier.maxQuantity && (
                                  <CounterInput
                                    count={quantity}
                                    setCount={setQuantity}
                                    min={tier.minQuantity ?? 1}
                                    max={tier.maxQuantity ?? 99}
                                    className="w-full"
                                  />
                                )}
                              </div>

                              <Button
                                onClick={() => setStep("purchase-confirmation")}
                              >
                                Continue
                              </Button>
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Configure Purchase */}
            {step === "purchase-confirmation" && (
              <div className="space-y-4">
                {/* Pricing details */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="font-medium mb-4">Purchase Summary</h3>
                  <div className="space-y-3 text-sm">
                    {/* Credit Pack Details */}
                    <div className="space-y-2 pb-3 border-b">
                      <div className="grid grid-cols-2">
                        <span className="text-muted-foreground">Credits:</span>
                        <span className="text-right font-mono font-medium">
                          {orderSummary?.quantity?.toLocaleString() || 0}
                        </span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-muted-foreground">Cost per Credit:</span>
                        <span className="text-right font-mono font-medium">
                          ${formatPricePerCredit(orderSummary?.pricePerTest ?? 0)}
                        </span>
                      </div>
                    </div>

                    {/* Pricing Breakdown */}
                    <div className="space-y-2">
                      {orderSummary && orderSummary.discount > 0 && (
                        <>
                          <div className="grid grid-cols-2">
                            <span className="text-muted-foreground">Base Price:</span>
                            <span className="text-right font-mono">
                              ${BasePrices[testType].toFixed(2)}/credit
                            </span>
                          </div>
                          <div className="grid grid-cols-2">
                            <span className="text-muted-foreground">
                              Original Price ({orderSummary.quantity?.toLocaleString()} × ${BasePrices[testType].toFixed(2)}):
                            </span>
                            <span className="text-right font-mono line-through text-muted-foreground">
                              ${(orderSummary.originalTotalPrice ?? 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2">
                            <span className="text-muted-foreground">
                              Discount ({orderSummary.discount.toFixed(1)}%):
                            </span>
                            <span className="text-right font-mono text-green-500">
                              -${(orderSummary.discountAmount ?? 0).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Total Price */}
                    <div className="pt-3 border-t">
                      <div className="grid grid-cols-2 items-center">
                        <span className="font-semibold text-base">Total Price (USD):</span>
                        <span className="text-right font-mono font-bold text-lg">
                          ${(orderSummary?.totalPrice ?? 0).toFixed(2)}
                        </span>
                      </div>
                      {orderSummary && orderSummary.discountAmount > 0 && (
                        <div className="grid grid-cols-2 mt-2">
                          <span className="text-sm text-green-600 dark:text-green-400">
                            Your Savings:
                          </span>
                          <span className="text-right font-mono text-sm text-green-600 dark:text-green-400 font-medium">
                            Save ${(orderSummary.discountAmount ?? 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="px-6">
            <Button variant="outline" onClick={handleBack}>
              {step !== "addon-selection" ? "Back" : "Cancel"}
            </Button>

            {step === "purchase-confirmation" && (
              <Button
                onClick={() => handlePurchase()}
                disabled={isLoadingCreateAddonTestSession}
              >
                {isLoadingCreateAddonTestSession ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Buy Now"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BuyMoreTestsDialog;
